import axios from "axios";
import { getLang } from "@/lib/i18n";

export const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`, withCredentials: true });
api.interceptors.request.use((c) => {
  c.headers["X-Lang"] = getLang();
  return c;
});

const NO_REFRESH = ["/auth/login", "/auth/register", "/auth/refresh"];
let refreshing = null;
api.interceptors.response.use((r) => r, async (error) => {
  const cfg = error.config || {};
  if (error.response?.status === 401 && !cfg._retry && !NO_REFRESH.includes(cfg.url)) {
    cfg._retry = true;
    refreshing = refreshing || api.post("/auth/refresh").finally(() => { refreshing = null; });
    try {
      await refreshing;
      return api(cfg);
    } catch {
      return Promise.reject(error);
    }
  }
  return Promise.reject(error);
});

export const apiError = (e, fallback = "Operazione non riuscita") => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg.replace("Value error, ", "");
  return fallback;
};

export const formatEuro = (n) =>
  `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const formatPickup = (iso) => {
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString(getLang(), { weekday: "short", day: "2-digit", month: "short" }),
    time: d.toLocaleTimeString(getLang(), { hour: "2-digit", minute: "2-digit" }),
  };
};

export const splitFare = (fare) => {
  const total = Number(fare) || 0;
  const executor = Math.round(total * 90) / 100;
  return { total, executor, platform: Math.round((total - executor) * 100) / 100 };
};

export const SERVICE_TYPES = [
  { value: "Rientro a Vuoto", label: "Rientro a Vuoto" },
  { value: "Transfer Urbano", label: "Transfer Urbano" },
  { value: "Transfer Aeroporto", label: "Aeroporto" },
  { value: "A Disposizione", label: "A Disposizione" },
];

export const VEHICLES = [
  { value: "Berlina Business", label: "Berlina Business" },
  { value: "Berlina Premium", label: "Berlina Premium" },
  { value: "Van 8 Posti", label: "Van 8 Posti" },
  { value: "Van VIP Luxury", label: "Van VIP Luxury" },
  { value: "Minibus 16+ Posti", label: "Minibus 16+ Posti" },
];

export const QUICK_VEHICLES = [
  { value: "Berlina Premium", label: "🚘 Berlina Premium" },
  { value: "Van 8 Posti", label: "🚐 Van 8 Posti" },
  { value: "Van VIP Luxury", label: "👑 VIP Luxury" },
];

export const downloadFile = async (path, filename, params) => {
  const { data } = await api.get(path, { responseType: "blob", params });
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export const networkInviteText = () =>
  `🚘 Ti invito su FLEETFLOW, la rete B2B riservata ad autisti NCC con licenza verificata: cedi e prendi corse in 2 tap, con deposito Escrow garantito e avvisi Smart Match. Entra qui: ${window.location.origin}`;

export const whatsappInviteUrl = () => `https://wa.me/?text=${encodeURIComponent(networkInviteText())}`;

export const rideLink = (ride) =>
  ride.invite_code
    ? `${window.location.origin}/?invite=${ride.invite_code}`
    : `${window.location.origin}/?ride=${ride.share_code}`;

export const whatsappUrl = (ride) => {
  const { date, time } = formatPickup(ride.pickup_at);
  const route = ride.invite_code
    ? "🔒 Corsa riservata con PIN (te lo invio a parte)"
    : ride.locked
      ? "📍 Tratta visibile ai colleghi iscritti a FLEETFLOW"
      : `📍 ${ride.origin} ➔ 🏁 ${ride.destination}`;
  const text = [
    "🚘 FLEETFLOW • Corsa B2B da passare",
    route,
    `🗓 ${date} • ${time}`,
    `🚐 ${ride.vehicle_category} • ${ride.passengers || 1} pax • ${ride.luggage || 0} valigie`,
    `💶 ${formatEuro(ride.fare)} • Pagamento in Escrow`,
    `Apri e blocca: ${rideLink(ride)}`,
  ].join("\n");
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
};

export const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};
